import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMaintenancePlan } from '../../hooks/useMaintenancePlan';
import { MaintenancePlan, MaintenanceTask } from '../../types/maintenance';
import { MaintenancePlanForm } from './MaintenancePlanForm';

interface MaintenancePlanListProps {
  vehicleId: string;
  organizationId: string;
}

export const MaintenancePlanList: React.FC<MaintenancePlanListProps> = ({
  vehicleId,
  organizationId,
}) => {
  const { t } = useTranslation();
  const { loading, error, deletePlan, getPlans } = useMaintenancePlan(vehicleId);

  const [plans, setPlans] = useState<MaintenancePlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<MaintenancePlan | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const fetchedPlans = await getPlans();
        setPlans(fetchedPlans);
      } catch (error) {
        console.error('Erreur lors de la récupération des plans:', error);
      }
    };

    fetchPlans();
  }, [vehicleId, organizationId, getPlans]);

  const handleCreateClick = () => {
    setSelectedPlan(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (plan: MaintenancePlan) => {
    setSelectedPlan(plan);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (plan: MaintenancePlan) => {
    setSelectedPlan(plan);
    setIsDeleteDialogOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedPlan(null);
  };

  const handleFormSubmit = async () => {
    setIsFormOpen(false);
    setSelectedPlan(null);
    try {
      const updatedPlans = await getPlans();
      setPlans(updatedPlans);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des plans:', error);
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedPlan) {
      try {
        await deletePlan(selectedPlan.id, organizationId);
        setPlans(plans.filter(p => p.id !== selectedPlan.id));
        setIsDeleteDialogOpen(false);
        setSelectedPlan(null);
      } catch (error) {
        console.error('Erreur lors de la suppression du plan:', error);
      }
    }
  };

  const renderTask = (task: MaintenanceTask) => (
    <Box key={task.id} sx={{ mb: 1 }}>
      <Typography variant="subtitle2">{task.name}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
        <Chip
          size="small"
          label={`${task.frequency.type === 'mileage' ? task.frequency.mileageValue + ' km' : task.frequency.timeValue + ' ' + t('maintenance.days')}`}
        />
        <Chip
          size="small"
          color={
            task.priority === 'critical'
              ? 'error'
              : task.priority === 'moderate'
              ? 'warning'
              : 'info'
          }
          label={t(`maintenance.priority.${task.priority}`)}
        />
      </Box>
    </Box>
  );

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {t('maintenance.error.loading')}: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{t('maintenance.plans.title')}</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleCreateClick}
          disabled={loading}
        >
          {t('maintenance.plans.create.title')}
        </Button>
      </Box>

      {loading ? (
        <Typography>{t('common.loading')}</Typography>
      ) : plans.length === 0 ? (
        <Typography color="textSecondary">
          {t('maintenance.plans.empty')}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {plans.map((plan) => (
            <Grid item xs={12} key={plan.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">{plan.name}</Typography>
                      {plan.description && (
                        <Typography color="textSecondary" variant="body2">
                          {plan.description}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(plan)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(plan)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box>
                    {plan.tasks.map(renderTask)}
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Chip
                      size="small"
                      color={plan.isAutoGenerated ? 'info' : 'default'}
                      label={
                        plan.isAutoGenerated
                          ? t('maintenance.plans.auto_generated')
                          : t('maintenance.plans.custom')
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={isFormOpen}
        onClose={handleFormClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <MaintenancePlanForm
            vehicleId={vehicleId}
            organizationId={organizationId}
            plan={selectedPlan || undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('maintenance.plans.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('maintenance.plans.delete.confirmation', {
              name: selectedPlan?.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
